// Notification management module for PPT Hero game
export default class NotificationManager {
  constructor(scene) {
    this.scene = scene;
  }

  // Show center notification
  showCenterNotification(text, type = 'info', duration = 2000) {
    try {
      // Remove existing notification
      if (this.scene.centerNotification) {
        this.scene.centerNotification.destroy();
      }
      
      // Set color based on type
      let color = '#ffffff';
      let backgroundColor = '#1a252f';
      
      switch (type) {
        case 'success':
          color = '#00ff88';
          backgroundColor = '#1a252f';
          break;
        case 'warning':
          color = '#ffaa00';
          backgroundColor = '#2a1a0f';
          break;
        case 'error':
          color = '#ff4444';
          backgroundColor = '#2a0f0f';
          break;
        case 'info':
        default:
          color = '#00d4ff';
          backgroundColor = '#1a252f';
          break;
      }
      
      // Create notification
      this.scene.centerNotification = this.scene.add.text(
        this.scene.cameras.main.width / 2,
        this.scene.cameras.main.height / 2,
        text,
        {
          ...this.scene.getProfessionalTextStyle("24px", color, "600"),
          backgroundColor: backgroundColor,
          padding: { x: 20, y: 15 },
          align: "center"
        }
      ).setOrigin(0.5).setDepth(1000);
      
      // Auto-remove after duration
      this.scene.time.delayedCall(duration, () => {
        if (this.scene.centerNotification) {
          this.scene.centerNotification.destroy();
          this.scene.centerNotification = null;
        }
      });
      
    } catch (error) {
      console.error('Error showing center notification:', error);
    }
  }



  // Hide success notification
  hideSuccessNotification() {
    if (this.scene.successBackground) {
      this.scene.successBackground.destroy();
      this.scene.successBackground = null;
    }
    if (this.scene.successText) {
      this.scene.successText.destroy();
      this.scene.successText = null;
    }
    if (this.scene.bonusText) {
      this.scene.bonusText.destroy();
      this.scene.bonusText = null;
    }
    if (this.scene.roundInfoText) {
      this.scene.roundInfoText.destroy();
      this.scene.roundInfoText = null;
    }
  }

  // Show message
  showMessage(text, duration = 1500) {
    this.showCenterNotification(text, 'info', duration);
  }

  // Show error message
  showError(text, duration = 3000) {
    this.showCenterNotification(text, 'error', duration);
  }

  // Show warning message
  showWarning(text, duration = 2500) {
    this.showCenterNotification(text, 'warning', duration);
  }

  // Show success message
  showSuccess(text, duration = 2000) {
    this.showCenterNotification(text, 'success', duration);
  }

  // Show temporary popup
  showPopup(text, duration = 2000, position = 'center') {
    try {
      let x, y;
      
      switch (position) {
        case 'top':
          x = this.scene.cameras.main.width / 2;
          y = 100;
          break;
        case 'bottom':
          x = this.scene.cameras.main.width / 2;
          y = this.scene.cameras.main.height - 100;
          break;
        case 'center':
        default:
          x = this.scene.cameras.main.width / 2;
          y = this.scene.cameras.main.height / 2;
          break;
      }
      
      const popup = this.scene.add.text(
        x,
        y,
        text,
        {
          ...this.scene.getProfessionalTextStyle("20px", "#ffffff", "500"),
          backgroundColor: "#1a252f",
          padding: { x: 15, y: 10 },
          align: "center"
        }
      ).setOrigin(0.5).setDepth(1000);
      
      // Auto-remove after duration
      this.scene.time.delayedCall(duration, () => {
        if (popup) {
          popup.destroy();
        }
      });
      
    } catch (error) {
      console.error('Error showing popup:', error);
    }
  }

  // Success notification for level completion - positioned same as shortcut notifications
  showSuccessNotification(roundName, levelNumberInRound, totalBonus, speedBonus) {
    try {
      // Clear existing level success notification
      if (this.scene.levelSuccessNotification) {
        this.scene.levelSuccessNotification.destroy();
        this.scene.levelSuccessNotification = null;
      }
      
      // Build structured completion message with consistent formatting
      const messages = [];
      
      // Main completion message
      messages.push(`🎉 ${roundName} Level ${levelNumberInRound} Complete!`);
      messages.push(`+${totalBonus} points • Score: ${this.scene.score}`);
      
      // Add bonus messages for exceptional performance
      if (this.scene.levelClicks === 0) {
        messages.push("🏆 PERFECT! Keyboard Only Bonus!");
      }
      
      const efficiency = this.scene.calculateEfficiency();
      if (efficiency >= 90) {
        messages.push("⚡ Highly Efficient Performance!");
      }
      
      // Show time bonus if applicable
      if (speedBonus > 0) {
        messages.push(`⏱️ Speed Bonus: +${speedBonus} pts`);
      }
      
      // Join with consistent line spacing
      const completionMessage = messages.join('\n');
      
      console.log('🎉 Showing success notification:', completionMessage);
      
      // Position at same location as shortcut notifications but with higher depth
      const slideArea = this.scene.slideArea;
      if (!slideArea) {
        console.warn('slideArea not found, falling back to center notification');
        this.showCenterNotification(completionMessage, 'success', 4500);
        return;
      }
      
      // Create Rex UI Label at same position as shortcut notifications
      this.scene.levelSuccessNotification = this.scene.rexUI.add.label({
        x: slideArea.x,
        y: slideArea.top - 50, // Same position as shortcut notifications
        
        background: this.scene.rexUI.add.roundRectangle(0, 0, 2, 2, 8, 0x27ae60), // Success green
        text: this.scene.add.text(0, 0, completionMessage, {
          fontSize: '16px',
          color: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          align: 'center'
        }),
        
        space: { left: 20, right: 20, top: 12, bottom: 12 }
      }).layout().setDepth(1600); // Higher depth than shortcut notifications (1500)
      
      // Auto-hide after longer duration for level completion
      this.scene.time.delayedCall(4500, () => {
        if (this.scene.levelSuccessNotification) {
          this.scene.levelSuccessNotification.destroy();
          this.scene.levelSuccessNotification = null;
        }
      });
      
    } catch (error) {
      console.error('Error showing level success notification:', error);
      // Fallback to center notification
      const completionMessage = `🎉 ${roundName} Level ${levelNumberInRound} Complete!\n+${totalBonus} points • Score: ${this.scene.score}`;
      this.showCenterNotification(completionMessage, 'success', 4500);
    }
  }

  // Clear all notifications
  clearAllNotifications() {
    if (this.scene.centerNotification) {
      this.scene.centerNotification.destroy();
      this.scene.centerNotification = null;
    }
    
    if (this.scene.levelSuccessNotification) {
      this.scene.levelSuccessNotification.destroy();
      this.scene.levelSuccessNotification = null;
    }
    
    this.hideSuccessNotification();
  }
}
